import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DraggableAttributes,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { SubtaskId, Task, TaskId } from "../../../shared/types";
import { TaskItem } from "./task-item/TaskItem";
import { EmptyState } from "../../../ui/atoms/EmptyState";

interface TaskListContentProps {
  readonly tasks: Task[];
  readonly showCategory: boolean;
  readonly selectedIds: Set<TaskId>;
  readonly onCycleStatus: (id: TaskId) => void;
  readonly onUpdate: (
    id: TaskId,
    updates: Partial<
      Pick<Task, "title" | "priority" | "notes" | "tags" | "dueDate">
    >,
  ) => void;
  readonly onDelete: (id: TaskId) => void;
  readonly onAddSubtask: (taskId: TaskId, title: string) => void;
  readonly onToggleSubtask: (taskId: TaskId, subtaskId: SubtaskId) => void;
  readonly onDeleteSubtask: (taskId: TaskId, subtaskId: SubtaskId) => void;
  readonly onSelect: (id: TaskId) => void;
  readonly onReorder?: (taskId: TaskId, newIndex: number) => void;
}

interface DragHandleProps {
  readonly attributes: DraggableAttributes;
  readonly listeners: Record<string, unknown> | undefined;
}

interface SortableTaskItemProps {
  readonly task: Task;
  readonly showCategory: boolean;
  readonly selectedIds: Set<TaskId>;
  readonly onCycleStatus: (id: TaskId) => void;
  readonly onUpdate: (
    id: TaskId,
    updates: Partial<
      Pick<Task, "title" | "priority" | "notes" | "tags" | "dueDate">
    >,
  ) => void;
  readonly onDelete: (id: TaskId) => void;
  readonly onAddSubtask: (taskId: TaskId, title: string) => void;
  readonly onToggleSubtask: (taskId: TaskId, subtaskId: SubtaskId) => void;
  readonly onDeleteSubtask: (taskId: TaskId, subtaskId: SubtaskId) => void;
  readonly onSelect: (id: TaskId) => void;
  readonly dragHandleProps?: DragHandleProps;
}

function SortableTaskItem({
  task,
  showCategory,
  selectedIds,
  onCycleStatus,
  onUpdate,
  onDelete,
  onAddSubtask,
  onToggleSubtask,
  onDeleteSubtask,
  onSelect,
}: SortableTaskItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={`transition-opacity ${isDragging ? "opacity-50" : ""}`}>
      <TaskItem
        task={task}
        showCategory={showCategory}
        selectable={selectedIds.size > 0}
        selected={selectedIds.has(task.id)}
        onCycleStatus={onCycleStatus}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onAddSubtask={onAddSubtask}
        onToggleSubtask={onToggleSubtask}
        onDeleteSubtask={onDeleteSubtask}
        onSelect={onSelect}
        dragHandleProps={{ attributes, listeners }}
      />
    </div>
  );
}

export function TaskListContentDnd({
  tasks,
  showCategory,
  selectedIds,
  onCycleStatus,
  onUpdate,
  onDelete,
  onAddSubtask,
  onToggleSubtask,
  onDeleteSubtask,
  onSelect,
  onReorder,
}: TaskListContentProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder) return;

    const oldIndex = tasks.findIndex((t) => t.id === active.id);
    const newIndex = tasks.findIndex((t) => t.id === over.id);

    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

    onReorder(active.id as TaskId, newIndex);
  };

  if (tasks.length === 0) {
    return (
      <EmptyState
        title="Aucune tâche"
        description="Commencez par ajouter votre première tâche."
        icon={
          <svg
            viewBox="0 0 24 24"
            className="w-12 h-12 text-ink-faint"
            stroke="currentColor"
            strokeWidth={2}
            fill="none"
          >
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      />
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      collisionDetection={closestCenter}
    >
      <SortableContext
        items={tasks.map((t) => t.id)}
        strategy={verticalListSortingStrategy}
      >
        <div role="list" aria-label="Liste des tâches" className="flex flex-col gap-2">
          {tasks.map((task) => (
            <SortableTaskItem
              key={task.id}
              task={task}
              showCategory={showCategory}
              selectedIds={selectedIds}
              onCycleStatus={onCycleStatus}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onAddSubtask={onAddSubtask}
              onToggleSubtask={onToggleSubtask}
              onDeleteSubtask={onDeleteSubtask}
              onSelect={onSelect}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

